import { useEffect, useRef } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { Button, Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { Typography } from 'antd';

import { CategoryTreeItem } from '~apis/categories';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { useCategoriesTree } from '~hooks/queries/categories/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';

import CategoryTreeSelect from './CategoryTreeSelect';
import MigrationListItem from './common/component/MigrationListItem';

interface Props {
  targetItems: FlattenTreeResult<CategoryTreeItem>[];
  depth: number;
  changeMap: Record<string, string>;
  setChangeMap: (value: Record<string, string>) => void;
  parentItem: FlattenTreeResult<CategoryTreeItem>;
}

const CategoryMigrationList: React.FC<Props> = ({
  targetItems,
  depth,
  changeMap,
  setChangeMap,
  parentItem,
}) => {
  const { companyId } = useDemoAwareCompanyId();
  const { data } = useCategoriesTree({ companyId });
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const targetItemKeys = targetItems.map((item) => item.key);

  const categories = data?.Data?.data?.tree?.children;
  const flattenedTree = flattenTree(categories ?? []).filter(
    (item) => !!item.value?.name && item.value.cashFlowType === parentItem.value?.cashFlowType,
  );
  const flattenedTreeOptions = flattenedTree.filter(
    (item) =>
      !!item.value?.name &&
      item.value.cashFlowType === parentItem.value?.cashFlowType &&
      item.key !== parentItem.key &&
      !item.value.contraCategoryId &&
      !targetItemKeys.includes(item.key), // 자신과 자신 하위계정은 제외
  );

  useEffect(() => {
    if (targetItems?.length > 0 && Object.values(changeMap).length === 0 && parentItem) {
      const initialMap: Record<string, string> = {};
      const cashFlowType = parentItem.value?.cashFlowType;
      targetItems.forEach((item) => {
        initialMap[item.value?.categoryLabelKey as string] =
          cashFlowType === 'IN' ? '84_null' : '95_null';
      });
      setChangeMap(initialMap);
    }
  }, [targetItems, changeMap, parentItem, setChangeMap]);

  return (
    <Flex direction="column" gap={12}>
      <Flex
        direction="column"
        style={{
          marginTop: 17,
        }}
      >
        <Typography.Text
          style={{
            ...typographyStyle['14_400'],
          }}
        >
          거래 내역과 자동화 규칙에 적용된 데이터를 이관할 계정을 선택해주세요.
        </Typography.Text>
      </Flex>
      <Flex direction="column" gap={8}>
        {targetItems.map((child) => {
          const target = flattenedTree.find((node) => node.key === child.key);

          const breadcrumbItems = target?.namePath
            .split('.')
            .slice(depth - 1, target.namePath.split('.').length)

            .map((key) => {
              return {
                key: key,
                title: key,
              };
            });

          const defaultActiveKey = flattenedTree.find(
            (item) =>
              item.value?.categoryLabelKey === changeMap[child.value?.categoryLabelKey as string],
          )?.key;

          return (
            <MigrationListItem
              breadcrumbItems={breadcrumbItems}
              key={child.key}
              treeSelectSection={
                <CategoryTreeSelect
                  disableScroll
                  treeRef={wrapperRef}
                  closeOnSelect
                  footerElement={<div />}
                  defaultActiveKey={defaultActiveKey ? [defaultActiveKey] : []}
                  style={{ flex: 1 }}
                  treeList={flattenedTreeOptions}
                  onSelect={(categoryLabelKey) => {
                    if (!child.value) return;
                    setChangeMap({
                      ...changeMap,
                      [child.value.categoryLabelKey as string]: categoryLabelKey,
                    });
                  }}
                  triggerElement={
                    <Button border>
                      <Flex
                        justify="space-between"
                        align="center"
                        style={{
                          width: '100%',
                        }}
                      >
                        <Typography.Text
                          style={{
                            color: Colors.gray800,
                            ...typographyStyle['14_400'],
                          }}
                        >
                          {changeMap[child.value?.categoryLabelKey as string]
                            ? flattenedTree.find(
                                (item) =>
                                  item.value?.categoryLabelKey ===
                                  changeMap[child.value?.categoryLabelKey as string],
                              )?.value?.name
                            : '계정 선택하기'}
                        </Typography.Text>
                        <DownOutlined
                          style={{
                            color: Colors.gray700,
                          }}
                        />
                      </Flex>
                    </Button>
                  }
                />
              }
            />
          );
        })}
      </Flex>
    </Flex>
  );
};

export default CategoryMigrationList;
